<template>
  <div>
    <BaseDialogContainer
      :on-primary-button-pressed="_onPrimaryButtonPressed"
      :on-cancel-button-pressed="onCancelButtonPressed"
      :title="title"
      :subtitle="subtitle"
      :primary-button-text="primaryButtonText"
      :dialog-model="dialog"
      :main-button-disabled="false"
    >
      <template #activatorButton="{ on: buttonOn, attrs: buttonAttrs }">
        <div class="dialog-activator-button">
          <v-btn
            icon
            small
            v-bind="buttonAttrs"
            v-on="buttonOn"
            @click="activateDialog"
          >
            <v-icon
              small
              style="color: var(--color-secondary-light)"
            >
              {{ icon }}
            </v-icon>
          </v-btn>
        </div>
      </template>

      <template #icon>
        <v-icon
          class="mr-1"
          color="primary"
        >
          {{ icon }}
        </v-icon>
      </template>

      <template #content>
        <v-form
          ref="form"
          v-model="valid"
        >
          <!-- Name -->
          <v-row no-gutters>
            <v-col cols="8">
              <TitledGenericInputField
                input-label="Name"
                icon-name="mdi-format-title"
                :value="profileData.profileName"
                :on-change="onNameChanged"
                :rules="[_validateName]"
              />
            </v-col>
          </v-row>

          <!-- Thermodynamic Properties -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Thermodynamic Properties
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <TitledNumericalInputField
                input-label="LHV (kJ/kg)"
                icon-name="mdi-fire"
                :on-change="onLowerHeatingValueChanged"
                :value="+profileData.lhv"
                :rules="[_validateLhv]"
              />
            </v-col>
          </v-row>

          <!-- Vessel Configuration -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Vessel Configuration
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="mr-2">
              <TitledNumericalInputField
                input-label="Trim (m)"
                icon-name="mdi-angle-obtuse"
                :on-change="onTrimChanged"
                :value="roundNumber(+profileData.trim, 3)"
                :rules="[_validateTrim]"
              />
            </v-col>
            <v-col>
              <TitledNumericalInputField
                input-label="Mean draft (m)"
                icon-name="mdi-waves"
                :on-change="onMeanDraftChanged"
                :value="roundNumber(+profileData.draft)"
                :rules="[_validateDraft]"
              />
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="mt-2"
          >
            <v-col>
              <TitledNumericalInputField
                input-label="Fouling"
                icon-name="mdi-grass"
                :on-change="onFoulingChanged"
                :value="+profileData.fouling"
                :disabled="profileData.useLatestFouling"
              />
            </v-col>
          </v-row>

          <!-- Environmental Conditions -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Environmental Conditions
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="mr-2">
              <TitledNumericalInputField
                input-label="True wind (kn)"
                icon-name="mdi-weather-windy"
                :on-change="onTrueWindChanged"
                :value="+profileData.trueWind"
                :rules="[_validateTrueWind]"
              />
            </v-col>
            <v-col>
              <TitledSelectionField
                input-label="Wind direction"
                icon-name="mdi-windsock"
                :items="['Head Seas', 'Beam Seas', 'Following Seas']"
                :on-change="onWindDirectionChanged"
                :value="profileData.windDirection"
              />
            </v-col>
          </v-row>

          <!-- Operational Parameters -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Operational Parameters
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <TitledGenericInputField
                input-label="STW (kn)"
                icon-name="mdi-speedometer"
                :on-change="onSpeedThroughWaterChanged"
                :value="profileData.valuesList"
                :rules="[_validateStw]"
              />
            </v-col>
          </v-row>

          <!-- Temporal Range -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Temporal Range
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <div
                class="d-flex align-center mb-2"
                style="color: var(--color-text)"
              >
                <v-icon color="var(--color-text)">
                  mdi-calendar
                </v-icon>
                <span
                  class="text-subtitle-1 font-weight-light mr-1"
                  style="color: var(--color-text)"
                >Date range</span>
                <span
                  class="text-subtitle-2 font-weight-light mr-1"
                  style="color: var(--color-text)"
                >(min - max)</span>
              </div>
              <v-dialog
                ref="dateDialog"
                v-model="dateDialog"
                persistent
                width="290px"
                :return-value.sync="dateModel"
              >
                <template v-slot:activator="{ on, attrs }">
                  <ThemedTextField
                    outlined
                    readonly
                    persistent-placeholder
                    hide-details="auto"
                    v-bind="attrs"
                    :value="displayDate(dateModel)"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="dateModel"
                  scrollable
                  range
                  :value="[profileData.minDate, profileData.maxDate]"
                  @change="onChange"
                >
                  <v-spacer />
                  <v-btn
                    text
                    color="primary"
                    @click="dateDialog = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.dateDialog.save(dateModel)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <!-- Advanced Options -->
          <v-row
            no-gutters
            class="mt-4"
          >
            <v-col cols="12">
              <v-divider class="mb-3" />
              <div class="section-header mb-3">
                Advanced Options
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>
              <TitledSwitchField
                :value="profileData.useLatestFouling"
                label="Use Latest Fouling"
                :disabled="fetchingLatestFouling"
                @change="onUseLatestFoulingChanged"
              />
            </v-col>
          </v-row>

          <!-- Snackbars -->
          <v-row
            no-gutters
            class="mt-3"
          >
            <v-col class="mr-2">
              <RequestResponseSnackBar
                v-model="_showSuccessSnackBar"
                :value="_showSuccessSnackBar"
                text="Profile updated successfully"
                color="green"
              />
              <RequestResponseSnackBar
                v-model="_showErrorSnackBar"
                :value="_showErrorSnackBar"
                text="Error in updating profile"
                color="red"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>

      <template #primaryButton>
        <v-btn
          color="primary"
          rounded
          block
          :loading="requestLoading"
          :disabled="!valid"
          @click="_onPrimaryButtonPressed"
        >
          {{ primaryButtonText }}
        </v-btn>
      </template>

      <template #cancelButton>
        <v-btn
          color="primary"
          block
          rounded
          text
          @click="onCancelButtonPressed"
        >
          Close
        </v-btn>
      </template>
    </BaseDialogContainer>
  </div>
</template>

<script>
import BaseDialogContainer from "../../../core/BaseDialogContainer.vue";
import TitledGenericInputField from "../components/TitledGenericInputField.vue";
import TitledNumericalInputField from "../components/TitledNumericalInputField.vue";
import TitledSelectionField from "../components/TitledSelectionField.vue";
import TitledSwitchField from "../components/TitledSwitchField.vue";
import RequestResponseSnackBar from "../../weather_routing/components/RequestResponseSnackBar.vue";
import ThemedTextField from "../../../core/ThemedTextField.vue";
import roundNumber from "../../../helper_functions/round_number";

export default {
  name: "PresetsEditingBaseView",
  components: {
    BaseDialogContainer,
    TitledGenericInputField,
    TitledNumericalInputField,
    TitledSelectionField,
    TitledSwitchField,
    RequestResponseSnackBar,
    ThemedTextField,
  },
  props: {
    profileData: Object,
    onPrimaryButtonPressed: Function,
    onNameChanged: Function,
    onLowerHeatingValueChanged: Function,
    onMeanDraftChanged: Function,
    onTrimChanged: Function,
    onTrueWindChanged: Function,
    onWindDirectionChanged: Function,
    onSpeedThroughWaterChanged: Function,
    onFoulingChanged: Function,
    onDateChanged: Function,
    subtitle: String,
    title: String,
    primaryButtonText: String,
    icon: String,
    requestLoading: Boolean,
    buttonDisabled: Boolean,
    onDialogActivated: Function,
    showSuccessSnackBar: Boolean,
    showErrorSnackBar: Boolean,
    onSnackBarClosed: Function,
  },
  data() {
    return {
      dialog: false,
      dateDialog: false,
      valid: true,
      dateModel: [],
      fetchingLatestFouling: false,
    };
  },

  computed: {
    _showSuccessSnackBar: {
      get() {
        return this.showSuccessSnackBar;
      },
      set() {
        this.onSnackBarClosed && this.onSnackBarClosed();
      },
    },
    _showErrorSnackBar: {
      get() {
        return this.showErrorSnackBar;
      },
      set() {
        this.onSnackBarClosed && this.onSnackBarClosed();
      },
    },
  },
  methods: {
    roundNumber,
    onCancelButtonPressed() {
      this.dialog = false;
    },
    async activateDialog() {
      this.dialog = true;
      this.onDialogActivated && this.onDialogActivated();

      // Let the parent's SET_PROFILE_DATA commit flush into profileData
      await this.$nextTick();

      if (this.profileData && this.profileData.useLatestFouling) {
        this._fetchAndApplyLatestFouling();
      }
    },
    async _fetchAndApplyLatestFouling() {
      try {
        this.fetchingLatestFouling = true;
        const latest = await this.$store.dispatch(
          "kDiagram/fetchLatestFouling",
          this.$route.params.vessel
        );
        if (latest !== null && this.onFoulingChanged) {
          this.onFoulingChanged(Number(latest));
          return true;
        }
        return false;
      } catch (e) {
        return false;
      } finally {
        this.fetchingLatestFouling = false;
      }
    },
    async onUseLatestFoulingChanged(value) {
      this.$store.commit("kDiagram/profiles/editProfile/SET_PROFILE_DATA_ATTRIBUTE", {
        attribute: "useLatestFouling",
        value,
      });

      if (value) {
        await this._fetchAndApplyLatestFouling();
      }
    },
    validateForm() {
      return this.$refs.form.validate();
    },
    _validateStw(values) {
      const regex = /^[0-9]+(\.[0-9]+)?(,[0-9]+(\.[0-9]+)?)*$/;
      return regex.test(values) || "Insert comma separated values ex: 9,9.5,10";
    },
    _validateName(value) {
      return value.length > 0 || "Name is required";
    },
    _validateLhv(value) {
      return (value > 40000 && value < 50000) || "LHV must be [40,50] MJ/kg";
    },
    _validateTrim(value) {
      return (value > -5 && value < 5) || "Trim must be [-5,5]";
    },
    _validateDraft(value) {
      return (value > 0 && value < 30) || "Draft must be in [0,30]";
    },
    _validateTrueWind(value) {
      return (value > 0 && value < 10) || "True wind must be in [0,10]";
    },
    _onPrimaryButtonPressed() {
      if (this.validateForm()) {
        this.onPrimaryButtonPressed && this.onPrimaryButtonPressed(this.profileData);
      }
    },
    onChange(date) {
      if (this.onDateChanged && date && date.length >= 2) {
        const sortedDates = [...date].sort((a, b) => new Date(a) - new Date(b));
        this.onDateChanged({ minDate: sortedDates[0], maxDate: sortedDates[1] });
      }
    },
    displayDate(dateRange) {
      if (!dateRange[0] || !dateRange[1]) return "";
      return [...dateRange].sort((a, b) => new Date(a) - new Date(b)).join(" - ");
    },
  },
};
</script>

<style scoped>
.dialog-activator-button {
  background-color: var(--color-bg);
  width: fit-content;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 0 4px;
  z-index: 1;
  transition: border-color var(--transition-default);
}

.dialog-activator-button:hover {
  border-color: var(--color-secondary-light);
}

.section-header {
  font-weight: 500;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text);
  opacity: 0.7;
}
</style>
